import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CodePanel } from '@/components/canvas/CodePanel';
import { CommentPopover } from '@/components/canvas/CommentLayer';
import { DeviceFrameSelector } from '@/components/canvas/DeviceFrame';
import { ExportMenu } from '@/components/canvas/ExportMenu';
import { PreviewButton } from '@/components/canvas/PreviewButton';
import { Toolbar } from '@/components/canvas/Toolbar';
import { TweaksPanel } from '@/components/canvas/TweaksPanel';
import { VersionHistory } from '@/components/canvas/VersionHistory';
import { buildArtifactHtml, HELLO_WORLD_ARTIFACT } from '@/lib/canvas-host';
import { deviceFrameWidth } from '@/lib/device-frames';
import {
  makeHostMessage,
  parseIframeMessage,
  type HostMessage,
  type IframeMessage,
} from '@/lib/postmessage';
import { subscribeArtifactChunks, useApp } from '@/store/useApp';

export function Canvas() {
  const iframeSrcDoc = useApp((s) => s.iframeSrcDoc);
  const artifactRevision = useApp((s) => s.artifactRevision);
  const isStreaming = useApp((s) => s.isStreaming);
  const deviceFrame = useApp((s) => s.deviceFrame);
  const mode = useApp((s) => s.mode);
  const edits = useApp((s) => s.edits);
  const tweakValues = useApp((s) => s.tweakValues);
  const setPendingComment = useApp((s) => s.setPendingComment);
  const applyEdit = useApp((s) => s.applyEdit);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const queueRef = useRef<HostMessage[]>([]);
  const readyRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [runtimeVersion, setRuntimeVersion] = useState<string | null>(null);

  const effectiveHtml = iframeSrcDoc || HELLO_WORLD_ARTIFACT;
  const srcDoc = useMemo(() => buildArtifactHtml(effectiveHtml), [effectiveHtml]);

  const sendToIframe = useCallback((msg: HostMessage) => {
    if (readyRef.current && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(msg, '*');
    } else {
      queueRef.current.push(msg);
    }
  }, []);

  // Subscribe to streamed artifact chunks → patch-append into the iframe (no remount).
  // Chunks are forwarded synchronously: any deferral here (rAF, setTimeout) creates a
  // race where a late chunk lands in the freshly-remounted post-stream iframe and
  // duplicates content.
  useEffect(() => {
    return subscribeArtifactChunks((chunk) => {
      if (!useApp.getState().isStreaming) return;
      sendToIframe(makeHostMessage('patch-append', { chunk }));
    });
  }, [sendToIframe]);

  useEffect(() => {
    setReady(false);
    readyRef.current = false;
    setRuntimeVersion(null);
    queueRef.current = [];
  }, [artifactRevision]);

  useEffect(() => {
    // Code mode is purely host-side (HTML editor in a side panel); the iframe
    // runtime doesn't need to be told. Map it to 'select' for the iframe so its
    // click/edit listeners go quiet.
    const iframeMode = mode === 'code' ? 'select' : mode;
    sendToIframe(makeHostMessage('enter-mode', { mode: iframeMode }));
  }, [mode, sendToIframe]);

  useEffect(() => {
    if (tweakValues.length === 0) return;
    sendToIframe(
      makeHostMessage('apply-tweaks', {
        values: tweakValues.map((t) => ({ variable: t.variable, value: t.value })),
      }),
    );
  }, [tweakValues, sendToIframe]);

  useEffect(() => {
    if (edits.length === 0) return;
    sendToIframe(
      makeHostMessage('apply-edits', {
        edits: edits.map((e) => ({ selector: e.selector, newText: e.newText })),
      }),
    );
  }, [edits, sendToIframe]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      const msg: IframeMessage | null = parseIframeMessage(event.data);
      if (!msg) return;
      if (msg.type === 'ready') {
        setReady(true);
        readyRef.current = true;
        setRuntimeVersion(msg.payload.runtimeVersion);
        const win = iframeRef.current.contentWindow;
        if (win) {
          const currentMode = useApp.getState().mode;
          const iframeMode = currentMode === 'code' ? 'select' : currentMode;
          win.postMessage(makeHostMessage('enter-mode', { mode: iframeMode }), '*');
          const stateEdits = useApp.getState().edits;
          if (stateEdits.length > 0) {
            win.postMessage(
              makeHostMessage('apply-edits', {
                edits: stateEdits.map((e) => ({ selector: e.selector, newText: e.newText })),
              }),
              '*',
            );
          }
          const stateTweaks = useApp.getState().tweakValues;
          if (stateTweaks.length > 0) {
            win.postMessage(
              makeHostMessage('apply-tweaks', {
                values: stateTweaks.map((t) => ({ variable: t.variable, value: t.value })),
              }),
              '*',
            );
          }
          for (const queued of queueRef.current) win.postMessage(queued, '*');
        }
        queueRef.current = [];
      } else if (msg.type === 'element-clicked') {
        setPendingComment({
          selector: msg.payload.selector,
          snippet: msg.payload.snippet,
          rect: msg.payload.rect,
        });
      } else if (msg.type === 'text-edited') {
        void applyEdit({
          selector: msg.payload.selector,
          oldText: msg.payload.oldText,
          newText: msg.payload.newText,
        });
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [setPendingComment, applyEdit]);

  return (
    <section
      aria-label="Canvas"
      className="relative flex h-full flex-col bg-neutral-50 dark:bg-neutral-900"
    >
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-4 py-2 dark:border-neutral-800">
        <div className="flex items-center gap-3">
          <p className="text-xs font-medium uppercase tracking-widest text-neutral-500">Canvas</p>
          <Toolbar />
          <VersionHistory />
        </div>
        <div className="flex items-center gap-2">
          <PreviewButton />
          <ExportMenu />
          <DeviceFrameSelector />
          <p className="min-w-20 text-right text-[11px] text-neutral-400">
            {isStreaming
              ? 'streaming…'
              : ready
                ? `runtime ${runtimeVersion ?? '?'}`
                : 'loading…'}
          </p>
        </div>
      </header>
      <div className="flex flex-1 overflow-hidden">
        <div className="relative flex flex-1 items-start justify-center overflow-auto bg-neutral-100 p-3 dark:bg-neutral-900">
          <div
            className="relative h-full w-full max-w-full overflow-hidden rounded-md border border-neutral-200 bg-white shadow-sm dark:border-neutral-800"
            style={{ width: deviceFrameWidth(deviceFrame), maxWidth: '100%' }}
          >
            <iframe
              key={artifactRevision}
              ref={iframeRef}
              title="Design canvas"
              sandbox="allow-scripts"
              srcDoc={srcDoc}
              className="h-full w-full border-0"
            />
            {isStreaming && <StreamingPulse />}
          </div>
        </div>
        {mode === 'tweaks' && <TweaksPanel />}
        {mode === 'code' && <CodePanel />}
      </div>
      <CommentPopover iframeRef={iframeRef} />
    </section>
  );
}

function StreamingPulse() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 rounded-md ring-2 ring-blue-400/30 ring-inset"
    >
      <div className="absolute right-2 top-2 flex items-center gap-1.5 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-medium text-blue-700 shadow ring-1 ring-blue-200 dark:bg-neutral-950/85 dark:text-blue-300 dark:ring-blue-900">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-500" />
        </span>
        live
      </div>
    </div>
  );
}
